import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CustomSelect } from "@/components/ui/custom-select";
import { DateRangeFilter } from "@/components/ui/date-range-filter";
import { EmptyState, ErrorState, Text } from "@/components/ui/text";
import { Fab } from "@/components/ui/fab";
import { FilterChip } from "@/components/ui/filter-chip";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { Icon } from "@/components/ui/icons";
import { LoadId } from "@/components/ui/load-id";
import { PageLoadingOverlay } from "@/components/ui/page-loading-overlay";
import { PageTitle } from "@/contexts/page-title-context";
import { EntityStatusBadge } from "@/components/shared/entity-status-badge";
import { InteractiveCard } from "@/components/ui/interactive-card";
import { CompactCardSkeleton } from "@/components/ui/compact-card-skeleton";
import { TableViewport } from "@/components/ui/table-viewport";
import { StickyHeaderSlot } from "@/components/ui/sticky-header-slot";
import { VStack } from "@/components/ui/v-stack";
import { HStack } from "@/components/ui/h-stack";
import { DividedList } from "@/components/ui/divided-list";
import {
  usePagination,
  usePageOffset,
  DEFAULT_PAGE_SIZE,
} from "@/hooks/use-pagination";
import { useSizeClass } from "@/hooks/use-size-class";
import { fromIntlLanguage } from "@/lib/i18n/config";
import { getLocalizedName } from "@/lib/localization";
import { getLoads } from "@/lib/api/loads";
import { getBrokerShippers } from "@/lib/api/broker-parties";
import { useUserEntity } from "@/contexts/user-entity-context";
import { formatDate, formatTonnage, type UnitLabels } from "@/lib/formatting";
import type { AppLanguage } from "@/lib/i18n";
import type { Load } from "@/lib/api/loads";
import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useFilterSearchParams } from "@/hooks/use-filter-search-params";
import { highlight, highlightNumber } from "@/lib/highlight";
import { loadSearchableFields } from "@/lib/search/load";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { LoadListTable } from "@/components/loads/load-list-table";
import { LoadListTableSkeleton } from "@/components/loads/load-list-table-skeleton";
import { useUrlSelection } from "@/hooks/use-url-selection";

function LoadsPage() {
  const sizeClass = useSizeClass();
  const isCompact = sizeClass === "compact";
  const { t: tLoads } = useTranslation("loads");
  const { t: tCommon } = useTranslation("common");
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const navigate = useNavigate();
  const { filters, setFilter, setFilters, clearFilters } =
    useFilterSearchParams({
      keys: ["status", "search", "date_from", "date_to", "shipper"],
      defaults: { status: "all" },
    });
  const { entity: userEntity } = useUserEntity();
  const isBroker = userEntity?.type === "Broker";
  const selectedShipperId = filters.shipper ?? "";
  const { data: brokerShippersData, isLoading: isLoadingShippers } = useQuery({
    queryKey: queryKeys.shippers({ limit: 100 }),
    queryFn: () => getBrokerShippers({ limit: 100 }),
    enabled: isBroker,
    staleTime: 5 * 60_000,
  });
  const brokerShippers = brokerShippersData?.data ?? [];
  const VALID_STATUSES = [
    "all",
    "PendingApproval",
    "Declined",
    "Listed",
    "Negotiating",
    "Negotiated",
    "InTransit",
    "Delivered",
    "incidents",
  ];
  const activeStatus = VALID_STATUSES.includes(filters.status)
    ? filters.status
    : "all";
  const dateRange = useMemo(
    () => ({
      from: filters.date_from || undefined,
      to: filters.date_to || undefined,
    }),
    [filters.date_from, filters.date_to],
  );
  const searchQuery = filters.search;

  // FilterToolbar already debounces draft → URL commits at 300ms via
  // useDebouncedSync, so searchQuery here is the committed (debounced) value.
  // An inner useDebounce would add a redundant second layer.
  const q = useMemo(() => {
    const trimmed = searchQuery.trim();
    return trimmed.length >= 3 ? trimmed : undefined;
  }, [searchQuery]);

  const { offset } = usePageOffset();
  const {
    data,
    isLoading: loading,
    isError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.loads({
      limit: DEFAULT_PAGE_SIZE,
      offset,
      status: activeStatus !== "all" ? [activeStatus] : undefined,
      shipper_id: isBroker && selectedShipperId ? selectedShipperId : undefined,
      q,
      load_date_from: dateRange.from,
      load_date_to: dateRange.to,
    }),
    queryFn: () =>
      getLoads({
        limit: DEFAULT_PAGE_SIZE,
        offset,
        status: activeStatus !== "all" ? [activeStatus] : undefined,
        shipper_id:
          isBroker && selectedShipperId ? selectedShipperId : undefined,
        q,
        load_date_from: dateRange.from,
        load_date_to: dateRange.to,
      }),
  });
  const loads = useMemo(() => data?.data ?? [], [data]);
  const total = data?.total ?? 0;
  const { page, totalPages, setPage } = usePagination({ total });

  const { selectedId: selectedLoadId, select: selectLoad } = useUrlSelection(
    "selectedLoadId",
    loads,
    loading,
  );

  const setStatusFilter = (status: string) => {
    setFilter("status", status);
  };

  const loadTabs = [
    { id: "all", label: tLoads("all") },
    { id: "PendingApproval", label: tLoads("statusPendingApproval") },
    { id: "Declined", label: tLoads("statusDeclined") },
    { id: "Listed", label: tLoads("statusListed") },
    { id: "Negotiating", label: tLoads("statusNegotiating") },
    { id: "Negotiated", label: tLoads("statusNegotiated") },
    { id: "InTransit", label: tLoads("statusInTransit") },
    { id: "Delivered", label: tLoads("statusDelivered") },
    { id: "incidents", label: tLoads("incidents") },
  ];

  const statusOptions = loadTabs.map((tab) => ({
    value: tab.id,
    label:
      tab.id === activeStatus && activeStatus !== "all"
        ? `${tab.label} (${total})`
        : tab.label,
  }));

  const activeAdvancedFilterCount =
    (dateRange.from || dateRange.to ? 1 : 0) +
    (isBroker && selectedShipperId ? 1 : 0);

  return (
    <VStack gap="lg" data-testid="loads-page">
      <PageTitle>{tLoads("title")}</PageTitle>
      {/* Page header */}
      <StickyHeaderSlot data-testid="loads-header">
        <Card
          data-testid="loads-header-actions-card"
          variant="bordered"
          padding="xs"
        >
          <FilterToolbar
            data-testid="loads-header-actions"
            searchPlaceholder={tLoads("searchPlaceholder")}
            searchValue={searchQuery}
            onSearchChange={(v) => setFilter("search", v)}
            pagination={{
              page,
              totalPages,
              perPage: DEFAULT_PAGE_SIZE,
              onPageChange: setPage,
              loading,
            }}
            quickFilters={
              <>
                {statusOptions.map((opt) => (
                  <FilterChip
                    key={opt.value}
                    active={activeStatus === opt.value}
                    onClick={() => setStatusFilter(opt.value)}
                    data-testid={`loads-filter-status-${opt.value}`}
                  >
                    {opt.label}
                  </FilterChip>
                ))}
              </>
            }
            advancedFilters={[
              ...(isBroker
                ? [
                    <CustomSelect
                      key="broker-shipper-filter"
                      data-testid="loads-broker-shipper-filter"
                      size="md"
                      value={selectedShipperId}
                      onChange={(value) => setFilter("shipper", value)}
                      disabled={isLoadingShippers}
                      emptyLabel="filter"
                      options={brokerShippers.map((s) => ({
                        value: s.id,
                        label: s.name,
                      }))}
                      label={tLoads("shipperFilter")}
                    />,
                  ]
                : []),
              <DateRangeFilter
                key="date-filter"
                data-testid="loads-date-filter"
                size="md"
                value={dateRange}
                onChange={(next) => {
                  setFilters({
                    date_from: next.from ?? "",
                    date_to: next.to ?? "",
                  });
                }}
                label={tLoads("loadDate")}
              />,
            ]}
            activeFilterCount={activeAdvancedFilterCount}
            onClearFilters={clearFilters}
            createAction={{
              label: tLoads("createLoad"),
              href: "/loads/new",
              testId: "loads-new-btn",
            }}
          />
        </Card>
      </StickyHeaderSlot>

      {/* Table / Card list */}
      <Card data-testid="loads-card" padding="none">
        <TableViewport data-testid="loads-table-container" position="relative">
          {loading && loads.length === 0 ? (
            isCompact ? (
              <CompactCardSkeleton count={6} />
            ) : (
              <LoadListTableSkeleton
                headers={[
                  tLoads("loadId"),
                  tLoads("originDestination"),
                  tLoads("details"),
                  tLoads("columnDateTags"),
                  tLoads("columnRatePayment"),
                  tLoads("status"),
                  tLoads("tickets"),
                  tLoads("actions"),
                ]}
                rows={6}
              />
            )
          ) : isError ? (
            <ErrorState
              title={tCommon("errorLoadingData")}
              action={
                <Button
                  color="neutral"
                  variant="outline"
                  onClick={() => void refetch()}
                >
                  {tCommon("retry")}
                </Button>
              }
              className="py-16"
            />
          ) : loads.length === 0 ? (
            <EmptyState
              title={tLoads("noLoadsTitle")}
              description={tLoads("noLoadsDescription")}
              icon={<Icon.Loads size="xl" />}
              className="py-16"
            />
          ) : isCompact ? (
            <LoadCardList
              loads={loads}
              language={language}
              q={q}
              onCardClick={(load) => {
                selectLoad(load.id);
                void navigate({ to: `/loads/${load.id}` });
              }}
            />
          ) : (
            <LoadListTable
              loads={loads}
              selectedLoadId={selectedLoadId}
              showTripProgress={true}
              showOffers={true}
              showTickets={true}
              showShipperColumn={isBroker}
              showActions={true}
              q={q}
              getLoadHref={(load) => `/loads/${load.id}`}
              onRowClick={(load) => {
                selectLoad(load.id);
                void navigate({ to: `/loads/${load.id}` });
              }}
            />
          )}
          {loading && loads.length > 0 && (
            <PageLoadingOverlay
              page={page}
              label={tCommon("pagination.loadingPage")}
            />
          )}
        </TableViewport>
      </Card>

      {/* FAB: create load (compact only) */}
      {isCompact && (
        <Fab
          href="/loads/new"
          aria-label={tLoads("createLoad")}
          data-testid="loads-create-fab"
        />
      )}
    </VStack>
  );
}

/* ------------------------------------------------------------------ */
/*  Compact card list                                                  */
/* ------------------------------------------------------------------ */

function LoadCardList({
  loads,
  language,
  onCardClick,
  q,
}: {
  loads: Load[];
  language: AppLanguage;
  onCardClick: (load: Load) => void;
  q?: string;
}) {
  const { t: tCommon } = useTranslation("common");
  const units: UnitLabels = {
    tonnageUnit: tCommon("tonnageUnit"),
    volumeUnit: tCommon("volumeUnit"),
  };
  return (
    <DividedList>
      {loads.map((load) => {
        const fields = loadSearchableFields(load, language);
        // Fall back to country when the first-stop city is empty.
        // `.value` check is required because every field is a Searchable
        // object (always truthy) — bare `||` would never fall back.
        const originLabel = fields.stop_origin_city.value
          ? fields.stop_origin_city
          : fields.country_origin;
        const destLabel = fields.stop_destination_city.value
          ? fields.stop_destination_city
          : fields.country_destination;
        const currencySymbol =
          load.currency?.symbol ||
          getLocalizedName(load.currency, language) ||
          "";

        return (
          <InteractiveCard
            key={load.id}
            data-testid={`load-card-${load.id}`}
            onClick={() => onCardClick(load)}
          >
            <HStack justify="between" gap="none">
              <LoadId>{highlight(fields.load_id, q)}</LoadId>
              <EntityStatusBadge
                entityType="load"
                status={load.status}
                searchable={fields.status}
                q={q}
              />
            </HStack>
            <Text>
              {originLabel.value ? highlight(originLabel, q) : "—"} →{" "}
              {destLabel.value ? highlight(destLabel, q) : "—"}
            </Text>
            <Text size="xs" color="muted">
              {formatDate(load.load_date, language)}
            </Text>
            <HStack justify="between" gap="none">
              <Text size="xs" color="muted">
                {highlight(fields.load_type, q)}
                {" · "}
                {formatTonnage(load.tonnage, units, language)}
              </Text>
              <Text weight="semibold">
                {currencySymbol}
                {highlightNumber(fields.price, q)}
              </Text>
            </HStack>
          </InteractiveCard>
        );
      })}
    </DividedList>
  );
}

export const Route = createFileRoute("/_app/loads/")({
  component: LoadsPage,
});
